// The single permission catalog, imported by BOTH the server (route guards, seed)
// and the client (UI `can()` guards). Effective access = super-admin bypass, else
// the role's hardcoded default set, then DB-backed grant/revoke overrides on top.

export const ROLES = [
  'super_admin',
  'manager',
  'data_entry',
  'production',
  'doctor_reviewer',
  'accountant',
  'clinic',
  'patient',
  'hr',
  'cad_design',
  'dispatch',
  'field_marketing',
  'viewer',
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super-admin',
  manager: 'Manager',
  data_entry: 'Data-entry',
  production: 'Production / Station',
  doctor_reviewer: 'Doctor-reviewer',
  accountant: 'Accountant / Finance',
  clinic: 'Clinic (external doctor)',
  patient: 'Patient',
  hr: 'HR',
  cad_design: 'CAD-design',
  dispatch: 'Dispatch',
  field_marketing: 'Marketing rep (field)',
  viewer: 'Viewer (read-only)',
};

/** Which audience/theme a role belongs to (drives routing + theming). */
export const ROLE_AUDIENCE: Record<Role, 'staff' | 'clinic' | 'patient' | 'hr' | 'field'> = {
  super_admin: 'staff',
  manager: 'staff',
  data_entry: 'staff',
  production: 'staff',
  doctor_reviewer: 'staff',
  accountant: 'staff',
  cad_design: 'staff',
  dispatch: 'staff',
  viewer: 'staff',
  hr: 'hr',
  clinic: 'clinic',
  patient: 'patient',
  // The field rep never sees the console: their whole app is the phone-first tracking portal.
  field_marketing: 'field',
};

export interface PermissionDef {
  key: string;
  group: string;
  description: string;
}

// Catalog grows as modules land. `domain:action` naming throughout.
export const PERMISSIONS = [
  // Cases
  { key: 'case:read', group: 'Cases', description: 'View cases and boards' },
  { key: 'case:create', group: 'Cases', description: 'Create cases' },
  { key: 'case:update', group: 'Cases', description: 'Edit case details' },
  { key: 'case:advance_stage', group: 'Cases', description: 'Advance a case to the next stage' },
  { key: 'case:approve_setup', group: 'Cases', description: 'Doctor sign-off of the digital setup (approval gate)' },
  { key: 'case:delete', group: 'Cases', description: 'Delete/void a case' },
  // Clinics
  { key: 'clinic:read', group: 'Clinics', description: 'View clinics' },
  { key: 'clinic:manage', group: 'Clinics', description: 'Create/edit clinics and portal access' },
  // Patients
  { key: 'patient:read', group: 'Patients', description: 'View patient records' },
  { key: 'patient:manage', group: 'Patients', description: 'Create/edit patient accounts' },
  { key: 'patient:review_media', group: 'Patients', description: 'Review patient-submitted media' },
  // Clinical grading & pricing
  { key: 'grading:read', group: 'Grading', description: 'View grades and classifications' },
  { key: 'grading:edit', group: 'Grading', description: 'Enter grade/classification/findings' },
  { key: 'grading:approve', group: 'Grading', description: 'Chief-only: approve & lock grade/price' },
  // Finance
  { key: 'finance:read', group: 'Finance', description: 'View ledgers, invoices, payments' },
  { key: 'finance:verify_payment', group: 'Finance', description: 'Verify and post payments' },
  { key: 'invoice:manage', group: 'Finance', description: 'Create/adjust invoices' },
  { key: 'expense:manage', group: 'Finance', description: 'Record expenses and supplier payments' },
  // The general ledger. Reading the books is a different privilege from posting to them, and
  // closing a month — which freezes reported figures — is a third.
  { key: 'accounting:read', group: 'Finance', description: 'View the general ledger, trial balance and financial statements' },
  { key: 'accounting:post', group: 'Finance', description: 'Post and reverse journal entries, and edit the chart of accounts' },
  { key: 'accounting:close', group: 'Finance', description: 'Close and reopen an accounting period' },
  // Manufacturing
  { key: 'manufacturing:read', group: 'Manufacturing', description: 'View print/production logs' },
  { key: 'manufacturing:log', group: 'Manufacturing', description: 'Create/update production logs' },
  // Notifications
  { key: 'notification:read', group: 'Notifications', description: 'View the notification outbox' },
  { key: 'notification:manage', group: 'Notifications', description: 'Retry/cancel notifications' },
  // Lab Workflow (WorkFlow 2026 master-case module)
  { key: 'workflow:read', group: 'Workflow', description: 'View workflow board, queues and case workspaces' },
  { key: 'workflow:create', group: 'Workflow', description: 'Create workflow cases' },
  { key: 'workflow:update', group: 'Workflow', description: 'Edit case fields, notes and files' },
  { key: 'workflow:move', group: 'Workflow', description: 'Move cases between stages / hold / resume' },
  { key: 'workflow:review', group: 'Workflow', description: 'Act in doctor review queues' },
  { key: 'workflow:approve', group: 'Workflow', description: 'Record doctor / production approvals' },
  { key: 'workflow:production', group: 'Workflow', description: 'Printing, touch-up, finishing, packaging records' },
  { key: 'workflow:dispatch', group: 'Workflow', description: 'Ready-for-dispatch and dispatch' },
  { key: 'workflow:cancel', group: 'Workflow', description: 'Cancel / reopen cases' },
  { key: 'workflow:bulk', group: 'Workflow', description: 'Bulk actions on many cases' },
  { key: 'workflow:import', group: 'Workflow', description: 'Import the Excel workbook' },
  { key: 'workflow:export', group: 'Workflow', description: 'Export cases / reports to Excel' },
  { key: 'workflow:reports', group: 'Workflow', description: 'Aging, workload and summary reports' },
  { key: 'workflow:configure', group: 'Workflow', description: 'Configure stages, options, doctors, SLAs, rules' },
  // Field marketing (rep tracking)
  { key: 'field:track', group: 'Field', description: 'Rep self-service: go on duty, send own location, log visits' },
  { key: 'field:read', group: 'Field', description: 'Lab side: see reps live, their trails, stops and dwell times' },
  { key: 'field:manage', group: 'Field', description: 'Manage reps, territories and known places (geofences)' },
  // Field CRM. Planning and chasing is what a rep does all day, so it rides on field:track; seeing
  // a whole team, and setting what they are measured against, are separate privileges.
  { key: 'field:plan', group: 'Field', description: 'Plan clinic visits and raise follow-ups' },
  { key: 'field:team', group: 'Field', description: 'See a team’s activity, coverage and performance' },
  { key: 'field:targets', group: 'Field', description: 'Set and change field targets' },
  // Platform / admin
  { key: 'audit:read', group: 'Admin', description: 'View the audit log' },
  { key: 'security:read', group: 'Admin', description: 'View the security / threat-detection dashboard' },
  { key: 'rbac:manage', group: 'Admin', description: 'Edit the role × permission matrix' },
  { key: 'user:manage', group: 'Admin', description: 'Create/edit users' },
  { key: 'settings:manage', group: 'Admin', description: 'Edit lab settings' },
] as const satisfies readonly PermissionDef[];

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

const ALL: PermissionKey[] = PERMISSIONS.map((p) => p.key);

/**
 * Hardcoded default matrix. `super_admin` is handled by bypass in code (gets everything),
 * so it is intentionally omitted here. DB overrides layer on top of these defaults.
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<Exclude<Role, 'super_admin'>, PermissionKey[]> = {
  manager: ALL.filter((p) => p !== 'rbac:manage' && p !== 'grading:approve'),
  data_entry: ['case:read', 'case:create', 'case:update', 'clinic:read', 'patient:read', 'patient:manage', 'workflow:read', 'workflow:create', 'workflow:update', 'workflow:move', 'workflow:export'],
  production: ['case:read', 'case:advance_stage', 'manufacturing:read', 'manufacturing:log', 'workflow:read', 'workflow:update', 'workflow:move', 'workflow:production'],
  doctor_reviewer: ['case:read', 'case:approve_setup', 'grading:read', 'grading:edit', 'grading:approve', 'patient:read', 'patient:review_media', 'workflow:read', 'workflow:update', 'workflow:move', 'workflow:review', 'workflow:approve'],
  // workflow:read is what gets them into the Lab Workflow console shell, where the doctor ledger and
  // payment-verification queue live (/workflow/billing, itself gated on finance:read).
  accountant: ['finance:read', 'finance:verify_payment', 'invoice:manage', 'expense:manage', 'accounting:read', 'accounting:post', 'accounting:close', 'clinic:read', 'workflow:read'],
  // The external treating doctor: creates cases and signs off the digital setup for their clinic.
  // Clinic portal: submit and track their own cases. Every workflow read is scoped to the clinic's
  // own doctor group server-side (see workflow/portal.ts), so these grants can't reach other clinics.
  clinic: ['case:read', 'case:create', 'case:approve_setup', 'patient:read', 'finance:read', 'workflow:read', 'workflow:create'],
  patient: [],
  hr: [],
  // A rep can only ever act on their OWN record — every field:track route resolves the rep
  // from the session, never from a client-supplied id. No console permission is granted.
  field_marketing: ['field:track', 'field:plan'],
  // Treatment planner / CAD: runs the planning stage, so needs case:update.
  cad_design: ['case:read', 'case:update', 'manufacturing:read', 'workflow:read', 'workflow:update', 'workflow:move'],
  dispatch: ['workflow:read', 'workflow:update', 'workflow:move', 'workflow:production', 'workflow:dispatch'],
  viewer: ['workflow:read', 'case:read', 'workflow:reports'],
};
